/**
 * installReportsHost — the one seam where HQ wires @intrasync/reports into its
 * composition root (called from createRootStore.ts right after the dashboard's
 * seam). Modelled on `views/dashboard/dashboardHost.ts`.
 *
 * Two things happen here, and each has a reason to be here:
 *
 * 1. `reportsModule.onInit(rootStore)` declares the tool's custom Feathers
 *    methods (`runs.execute`, `runs.preview`, `enablement.set`,
 *    `views.duplicate`, `schedules.runNow`) on `rootStore.app` and installs the
 *    record-level `rootStore.reportsClient`. It is GUARDED for
 *    `hqMode === "test"` the same way installDashboardHost is: under vitest
 *    `apiClient("test")` is a transportless `feathers().configure(authentication())`
 *    with no default service, so `app.service(path)` would throw from inside
 *    onInit. In test mode only the record-level client is installed (it reads
 *    nothing until asked). Paths the methods could not be declared on are
 *    reported through diagnostics, never swallowed — a skipped path means every
 *    Run is silently a 404 later.
 *
 * 2. THE PRINCIPAL. The tool reads `rootStore.authStore.currentUser` for the
 *    caller's plants: `allowedPlantIds`, where all-plant authority is an
 *    explicit `'*'` and an absence fails CLOSED. HQ is single-tenant with no
 *    plants, and its user payload carries no plant field, so without this the
 *    Admin panel (settings, availability, the company letterhead) renders
 *    read-only and a new run / view / schedule starts with no scope. The api
 *    side stamps the same `allowedPlantIds: ['*']` on its principal
 *    (api services/_shared/tool-principal.ts `decoratePrincipal`, shared by the
 *    dashboard and reports hosts), so this makes the browser agree with the server —
 *    it grants nothing: reports-api decides every write.
 *    `companyId` is NOT stamped here: nothing in @intrasync/reports' (or
 *    @intrasync/dashboard's) web reads it off the user — only from API rows —
 *    and copying HQ's company GUID into the web would be a second place for it
 *    to drift. `tier` is already on HQ's user payload, which is what the tool's
 *    admin check reads.
 *    Done as a MobX reaction on `authStore.user` (`currentUser` is a computed
 *    spread of it) because the user object is replaced on every sign-in and
 *    profile update. The user is never sent back to the api wholesale, and the
 *    dashboard sends plants only when a person narrowed them, so the stamp
 *    changes no request.
 *
 * 3. SIGN-OUT. The library parks its ReportsViewStore on
 *    `rootStore[reportsStoreSlot()]` (`ensureReportsStore`) — outside
 *    appStoresConfig, so RootStore.purgeUserData would never reach it, and the
 *    next person on the same tab would inherit the open run dialog (its preview
 *    and output file), the builder draft and its preview rows, the history
 *    filter. It is handed to `registerSessionStore` the way the AI agent chat's
 *    lazy store is: the purge calls `reset()`, which DISPOSES the parked store
 *    (its user reaction, a pending builder preview) and deletes the slot, so the
 *    next mount builds a fresh one. A different person signing in without a
 *    sign-out in between (the user id changes under a parked store) gets the
 *    same treatment.
 *
 * 4. WHAT TO OFFER. The tool asks `rootStore.modulePermissionsStore.isAllowed`
 *    which controls to offer and, with no such store, offers building, saving
 *    and sharing views to everyone — but the api floors those writes at operator
 *    (REPORTS_METHOD_TIERS in api reports-host.ts). HQ has no permissions store,
 *    so this installs a minimal one that answers the `reports.*` names by HQ
 *    tier (auth/tier.ts) and passes every other name — the dashboard reads the
 *    same store — to whatever answered before (a previous store, else the
 *    library's own `fallback ?? true`), so nothing outside reports changes. It
 *    only decides what is offered: reports-api still decides every write.
 */
import { reaction, runInAction } from "mobx";
import type { RootStore as LibraryRootStore } from "@intrasync/stores";
import {
  REPORTS_PERMISSIONS,
  currentUserRef,
  installReportsClient,
  registerReportsClientMethods,
  reportsModule,
  reportsStoreSlot,
  type FeathersClientLike,
} from "@intrasync/reports";
import { resolveTier, type TierUserLike, type UserTier } from "../../../auth/tier";

/** HQ's single tenant has every plant: the tool's explicit all-plant grant. */
export const HQ_PLANT_ACCESS: readonly string[] = Object.freeze(["*"]);

/** The slice of HQ's RootStore this installer reads. Structural so a test can stub it. */
export interface ReportsHostRootStore {
  app?: unknown;
  authStore?: { user?: object | undefined; currentUser?: unknown } | undefined;
  /** HQ's RootStore.registerSessionStore: purgeUserData resets what is handed to it. */
  registerSessionStore?: (name: string, store: { reset?: () => void }) => void;
  diagnostics?: {
    warn(store: string, code: "service.init_failed", message: string, detail?: { data?: Record<string, unknown> }): void;
    error(
      store: string,
      code: "service.init_failed",
      message: string,
      detail?: { error?: unknown; data?: Record<string, unknown> },
    ): void;
  };
  [key: string]: unknown;
}

export interface InstallReportsHostOptions {
  /** `hqMode` from rootStoreConfig.ts, passed in (see installDashboardHost for why). */
  mode: string;
}

const DIAG_STORE = "ReportsHost";

/** Does this user already carry the all-plant grant? */
const hasAllPlants = (user: { allowedPlantIds?: unknown }): boolean =>
  Array.isArray(user.allowedPlantIds) && (user.allowedPlantIds as unknown[]).includes("*");

/**
 * Stamp `allowedPlantIds: ['*']` on the signed-in user whenever it is (re)set.
 * Returns the reaction's disposer.
 */
export function decorateReportsPrincipal(rootStore: ReportsHostRootStore): () => void {
  return reaction(
    () => rootStore.authStore?.user,
    (user) => {
      const auth = rootStore.authStore;
      if (!auth || !user || typeof user !== "object" || hasAllPlants(user)) return;
      runInAction(() => {
        auth.user = { ...user, allowedPlantIds: [...HQ_PLANT_ACCESS] };
      });
    },
    { fireImmediately: true },
  );
}

// ─── Sign-out ───────────────────────────────────────────────────────────────

/**
 * Dispose the view store the library parked on this root (if any) and empty its
 * slot, so the next `ensureReportsStore` builds a fresh one for whoever is
 * signed in then. Idempotent.
 */
export function forgetReportsStore(rootStore: ReportsHostRootStore): void {
  const slot = reportsStoreSlot();
  const parked = rootStore[slot] as { dispose?: () => void } | null | undefined;
  delete rootStore[slot];
  if (parked && typeof parked.dispose === "function") parked.dispose();
}

/**
 * Forget the parked store when a DIFFERENT person is signed in under it
 * (A -> B without a purge in between). A sign-out (A -> nobody) is the purge's
 * job; nobody -> A has nothing to forget. Returns the reaction's disposer.
 */
function forgetOnUserSwitch(rootStore: ReportsHostRootStore): () => void {
  return reaction(
    () => currentUserRef(rootStore),
    (next, previous) => {
      if (previous !== undefined && next !== undefined && next !== previous) forgetReportsStore(rootStore);
    },
  );
}

// ─── What the hub offers ────────────────────────────────────────────────────

/** The `isAllowed(name, fallback)` contract @intrasync/reports and @intrasync/dashboard read off `rootStore.modulePermissionsStore`. */
export interface ModulePermissionsSource {
  isAllowed(name: string, fallback?: boolean): boolean;
}

/**
 * The lowest HQ tier each reports permission is offered to. `null` = any
 * signed-in staff tier (viewer and up). Mirrors the api's REPORTS_METHOD_TIERS:
 * runs are staff; views create/patch/remove/duplicate and view-shares
 * create/remove are operator; enablement, settings writes and schedule writes
 * are admin. `manageOrgViews` is admin because with no permission seam the
 * package itself falls back to the admin tier for it.
 */
export const HQ_REPORTS_PERMISSION_TIERS: Readonly<Record<string, UserTier | null>> = Object.freeze({
  [REPORTS_PERMISSIONS.runReports]: null,
  [REPORTS_PERMISSIONS.buildViews]: "operator",
  [REPORTS_PERMISSIONS.shareViews]: "operator",
  [REPORTS_PERMISSIONS.editSharedViews]: "operator",
  [REPORTS_PERMISSIONS.manageOrgViews]: "admin",
  [REPORTS_PERMISSIONS.scheduleReports]: "admin",
  [REPORTS_PERMISSIONS.manageReports]: "admin",
});

const TIER_RANK: Record<UserTier, number> = { viewer: 0, operator: 1, admin: 2 };

const isPermissionsSource = (value: unknown): value is ModulePermissionsSource =>
  !!value && typeof (value as { isAllowed?: unknown }).isAllowed === "function";

/**
 * A permissions source that answers ONLY the reports names, by the signed-in
 * user's HQ tier, and hands every other name to `previous` — or, with none,
 * returns what the libraries' `isPermitted` returned with no store at all
 * (`fallback ?? true`). Reads the user on every call, so a computed that asks
 * follows sign-in, sign-out and tier changes.
 */
export function createReportsPermissionSource(
  rootStore: ReportsHostRootStore,
  previous?: unknown,
): ModulePermissionsSource {
  const delegate = isPermissionsSource(previous) ? previous : null;
  return {
    isAllowed(name: string, fallback?: boolean): boolean {
      if (Object.prototype.hasOwnProperty.call(HQ_REPORTS_PERMISSION_TIERS, name)) {
        const tier = resolveTier(rootStore.authStore?.currentUser as TierUserLike | undefined);
        if (tier === null) return false;
        const floor = HQ_REPORTS_PERMISSION_TIERS[name];
        return floor === null || TIER_RANK[tier] >= TIER_RANK[floor];
      }
      return delegate ? delegate.isAllowed(name, fallback) : (fallback ?? true);
    },
  };
}

export function installReportsHost(
  rootStore: ReportsHostRootStore,
  app: FeathersClientLike,
  options: InstallReportsHostOptions,
): () => void {
  if (options.mode === "test") {
    // onInit would call registerReportsClientMethods on the transportless test
    // client and throw; install only the record-level client.
    installReportsClient(rootStore);
  } else {
    // Declare first, through the same client onInit uses, so a skipped path is
    // reported here; onInit's own (idempotent) pass then re-binds the same names.
    try {
      const skipped = registerReportsClientMethods(app);
      if (skipped.length > 0) {
        rootStore.diagnostics?.warn(
          DIAG_STORE,
          "service.init_failed",
          `Reports custom methods could not be declared on ${skipped.join(", ")}; runs and admin actions on those services will fail.`,
          { data: { skipped } },
        );
      }
    } catch (error) {
      rootStore.diagnostics?.error(
        DIAG_STORE,
        "service.init_failed",
        "Reports custom methods could not be declared on the Feathers client.",
        { error },
      );
    }
    try {
      reportsModule.onInit(rootStore as unknown as LibraryRootStore);
    } catch (error) {
      rootStore.diagnostics?.error(DIAG_STORE, "service.init_failed", "The reports module could not initialise.", {
        error,
      });
      installReportsClient(rootStore);
    }
  }

  const previousPermissions = rootStore.modulePermissionsStore;
  rootStore.modulePermissionsStore = createReportsPermissionSource(rootStore, previousPermissions);

  rootStore.registerSessionStore?.(reportsStoreSlot(), { reset: () => forgetReportsStore(rootStore) });
  const stopUserSwitch = forgetOnUserSwitch(rootStore);
  const stopPrincipal = decorateReportsPrincipal(rootStore);

  return () => {
    stopPrincipal();
    stopUserSwitch();
  };
}
